/**
 * GraphQL Fragments for component data requirements.
 * Each component declares which fragments it needs.
 */

/**
 * Inline media snapshot selected alongside every image field as `<field>_media`.
 * Mirrors IMAGE_MEDIA_SNAPSHOT in operations.ts.
 */
const IMAGE_MEDIA_SNAPSHOT = '{ alt_text public_id storage_key width height }';
const VIDEO_MEDIA_SNAPSHOT = '{ public_id storage_key thumbnail_key size_bytes classification title description }';

// ============================================================================
// Core Property Fragments
// ============================================================================

export const PropertyCoreFragment = /* GraphQL */ `
  fragment PropertyCore on Property {
    multihub_property_id
    property_name
    property_short_description
    property_phone
    property_email
    property_full_address
    property_address_city
    property_address_state
    property_address_zip
    property_address_latitude
    property_address_longitude
    property_website_url
    property_domain
  }
`;

export const PropertyBrandingFragment = /* GraphQL */ `
  fragment PropertyBranding on Property {
    property_primary_logo
    property_primary_logo_media ${IMAGE_MEDIA_SNAPSHOT}
    property_secondary_logo
    property_secondary_logo_media ${IMAGE_MEDIA_SNAPSHOT}
    property_mono_white_logo
    property_mono_white_logo_media ${IMAGE_MEDIA_SNAPSHOT}
    property_mono_black_logo
    property_mono_black_logo_media ${IMAGE_MEDIA_SNAPSHOT}
    property_fav_icon
    property_fav_icon_media ${IMAGE_MEDIA_SNAPSHOT}
    property_featured_image
    property_featured_image_media ${IMAGE_MEDIA_SNAPSHOT}
    property_featured_video
    property_featured_video_media ${VIDEO_MEDIA_SNAPSHOT}
    property_primary_color
    property_secondary_color
    property_font_color
    property_company_name
    property_company_icon
    property_company_icon_media ${IMAGE_MEDIA_SNAPSHOT}
    property_company_logo
    property_company_logo_media ${IMAGE_MEDIA_SNAPSHOT}
    property_company_url
  }
`;

export const PropertySocialFragment = /* GraphQL */ `
  fragment PropertySocial on Property {
    property_facebook_url
    property_instagram_url
    property_twitter_url
    property_linkedin_url
    property_youtube_url
    property_tiktok_url
    property_yelp_url
    property_google_business_url
  }
`;

export const PropertyUrlsFragment = /* GraphQL */ `
  fragment PropertyUrls on Property {
    property_apply_now_url
    property_resident_portal_url
    property_online_leasing_url
    property_apartments_url
  }
`;

export const PropertySEOFragment = /* GraphQL */ `
  fragment PropertySEO on Property {
    property_seo_title_home
    property_seo_title_floorplans
    property_seo_title_amenities
    property_seo_title_gallery
    property_seo_title_neighborhood
    property_seo_title_contact
    property_seo_title_virtualtours
    property_seo_description_home
    property_seo_description_floorplans
    property_seo_description_amenities
    property_seo_description_gallery
    property_seo_description_neighborhood
    property_seo_description_contact
    property_seo_description_virtualtours
  }
`;

export const PropertyBlurbsFragment = /* GraphQL */ `
  fragment PropertyBlurbs on Property {
    property_home_hero_title
    property_home_romance_title
    property_home_amenities_blurb
    property_home_floorplans_blurb
    property_home_gallery_blurb
    property_home_neighborhood_blurb
    property_floorplans_blurb
    property_amenities_blurb
    property_gallery_blurb
    property_neighborhood_blurb
    property_virtualtours_blurb
    property_contactus_blurb
  }
`;

export const PropertyPetPolicyFragment = /* GraphQL */ `
  fragment PropertyPetPolicy on Property {
    property_ispetfriendly
    property_allows_cats
    property_allows_dogs
    property_max_pets
    property_max_pet_weight_limit
    property_pet_deposit
    property_upfront_pet_fee
    property_monthly_pet_rent
    property_pet_restrictions
    property_pet_policy_description
    property_petpolicy_info
  }
`;

export const PropertyFeesFragment = /* GraphQL */ `
  fragment PropertyFees on Property {
    property_application_fee
    property_admin_fee
    property_fees_deposit_info
  }
`;

// ============================================================================
// Collection Fragments
// ============================================================================

export const AmenityFragment = /* GraphQL */ `
  fragment AmenityData on Amenity {
    title
    description
    group
    amenity_icon
    amenity_icon_media ${IMAGE_MEDIA_SNAPSHOT}
    amenity_image
    amenity_image_media ${IMAGE_MEDIA_SNAPSHOT}
    amenity_image_alt_text
    is_featured
  }
`;

export const FloorplanFragment = /* GraphQL */ `
  fragment FloorplanData on Floorplan {
    floorplan_id
    floorplan_name
    floorplan_image_url
    floorplan_image_url_media ${IMAGE_MEDIA_SNAPSHOT}
    floorplan_beds_min
    floorplan_beds_max
    floorplan_baths_min
    floorplan_baths_max
    floorplan_sqft_min
    floorplan_sqft_max
    floorplan_price_min
    floorplan_price_max
    floorplan_available_units
    floorplan_code
    floorplan_description
    floorplan_unit_count
    floorplan_deposit_min
    floorplan_deposit_max
    floorplan_max_occupants
    floorplan_is_disabled
    units {
      id
      unit_id
      unit_name
      floorplan_id
      floorplan_name
      bedrooms
      bathrooms
      sqft
      available
      available_date
      status
      price_min
      price_max
      rent_market
      deposit_min
      deposit_max
      is_furnished
      floor
      building
      make_ready_date
      max_occupants
      apply_url
      fees {
        id
        name
        description
        amount
        amount_max
        amount_raw
        amount_type
        timing
        category
        charge_code
        is_required
        is_refundable
        is_taxable
      }
      virtual_tours {
        id
        virtual_tour_title
        virtual_tour_description
        virtual_tour_thumbnail
        virtual_tour_thumbnail_media ${IMAGE_MEDIA_SNAPSHOT}
        virtual_tour_type
        virtual_tour_url
        url_health_error
      }
    }
    fees {
      id
      name
      description
      amount
      amount_max
      amount_raw
      amount_type
      timing
      category
      charge_code
      is_required
      is_refundable
      is_taxable
    }
    pet_policies {
      id
      pet_type
      allowed
      max_count
      weight_limit
      deposit
      fee
      monthly_rent
      description
      restrictions
    }
  }
`;

export const VirtualTourFragment = /* GraphQL */ `
  fragment VirtualTourData on VirtualTour {
    agency_id
    company_id
    property_id
    source
    url_health_error
    virtual_tour_description
    virtual_tour_thumbnail
    virtual_tour_thumbnail_media ${IMAGE_MEDIA_SNAPSHOT}
    virtual_tour_title
    virtual_tour_type
    virtual_tour_url
  }
`;

export const PhotoGalleryFragment = /* GraphQL */ `
  fragment PhotoGalleryData on PhotoGallery {
    image
    image_media ${IMAGE_MEDIA_SNAPSHOT}
    original_url
    title
    description
    alt_text
    source_page_urls
  }
`;

export const OfficeHoursFragment = /* GraphQL */ `
  fragment OfficeHoursData on OfficeHours {
    day
    open_time
    close_time
  }
`;

export const NeighborhoodPOIFragment = /* GraphQL */ `
  fragment NeighborhoodPOIData on NeighborhoodPOI {
    poi_name
    poi_type
    poi_address
    poi_latitude
    poi_longitude
    poi_short_description
    poi_long_description
    poi_featured_image
    poi_featured_image_media ${IMAGE_MEDIA_SNAPSHOT}
    poi_business_url
    poi_business_phone
    poi_directions_map_url
    poi_miles_from_property
    poi_walking_minutes
    poi_driving_minutes
    poi_biking_minutes
    poi_map_icon
    poi_map_icon_media ${IMAGE_MEDIA_SNAPSHOT}
    poi_google_place_id
    poi_place_primary_type
  }
`;

export const ReviewFragment = /* GraphQL */ `
  fragment ReviewData on Review {
    name
    title
    text
    rating
    date
    source
  }
`;

export const SpecialOfferFragment = /* GraphQL */ `
  fragment SpecialOfferData on SpecialOffer {
    title
    description
    disclaimer
    start_date
    end_date
    enabled
    lease_term
  }
`;

export const FaqFragment = /* GraphQL */ `
  fragment FaqData on Faq {
    question
    answer
    category
    source
  }
`;

// ============================================================================
// Composite Fragments (for full page loads)
// ============================================================================

export const PropertyFullFragment = /* GraphQL */ `
  fragment PropertyFull on Property {
    ...PropertyCore
    ...PropertyBranding
    ...PropertySocial
    ...PropertyUrls
    ...PropertySEO
    ...PropertyBlurbs
    ...PropertyPetPolicy
    ...PropertyFees
    property_year_built
    property_total_units
    property_bedrooms_range
    property_bathrooms_range
    property_sqft_range
    property_price_range
    property_walk_score
    property_transit_score
    property_copyright
  }
  ${PropertyCoreFragment}
  ${PropertyBrandingFragment}
  ${PropertySocialFragment}
  ${PropertyUrlsFragment}
  ${PropertySEOFragment}
  ${PropertyBlurbsFragment}
  ${PropertyPetPolicyFragment}
  ${PropertyFeesFragment}
`;

// ============================================================================
// Fragment Registry
// ============================================================================

/**
 * Maps component slots to their required fragments.
 * Used by the runtime to build optimized queries.
 */
export const componentFragmentRequirements: Record<string, string[]> = {
  Header: ['PropertyCore', 'PropertyBranding', 'PropertyUrls'],
  Footer: ['PropertyCore', 'PropertyBranding', 'PropertySocial'],
  Navigation: ['PropertyCore', 'PropertyUrls'],
  Hero: ['PropertyCore', 'PropertyBranding', 'PropertyBlurbs'],
  PageHero: ['PropertyCore', 'PropertyBranding'],
  OfficeHours: ['OfficeHoursData'],
  SocialHub: ['PropertySocial'],
  Gallery: ['PhotoGalleryData'],
  VirtualTourEmbed: ['VirtualTourData'],
  ContactForm: ['PropertyCore', 'OfficeHoursData', 'FloorplanData'],
  FloorplansList: ['FloorplanData'],
  AmenitiesList: ['AmenityData'],
  NeighborhoodMap: ['PropertyCore', 'PropertyBranding', 'NeighborhoodPOIData'],
  Reviews: ['ReviewData'],
  SpecialOffers: ['SpecialOfferData'],
  PetPolicy: ['PropertyPetPolicy'],
  Fees: ['PropertyFees'],
  CTAButton: [],
  CTASection: ['PropertyCore', 'PropertyUrls'],
  ResidentAccess: ['PropertyCore', 'PropertyUrls'],
  AccessibilityStatement: ['PropertyCore'],
  PropertyOverview: ['PropertyCore', 'PropertyBlurbs', 'PhotoGalleryData'],
};

